/** API Service Layer - Centralized API calls */

import { API_URL } from '../config/constants';
import { getStorageValue } from '../utils/helpers';

/**
 * Internal fetch wrapper with auth headers
 */
const fetchWithAuth = async (endpoint, options = {}) => {
  const role = localStorage.getItem('role') || 'helpdesk';
  const storagePrefix = {
    super_admin: 'superadmin',
    manager: 'manager',
    supervisor: 'supervisor',
    helpdesk_agent: 'helpdesk'
  }[role] || 'helpdesk';

  const token = localStorage.getItem(`${storagePrefix}Token`);

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    }
  });

  if (response.status === 401) {
    // Auto logout on unauthorized
    localStorage.clear();
    window.location.href = '/login';
  }

  return response;
};

// ========== Authentication ==========

export const authAPI = {
  /**
   * Login user
   */
  login: async (username, password) => {
    const res = await fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });
    return res.json();
  },

  /**
   * Logout user
   */
  logout: async () => {
    const res = await fetchWithAuth('/auth/logout', { method: 'POST' });
    return res.json();
  },

  /**
   * Get current user
   */
  getCurrentUser: async () => {
    const res = await fetchWithAuth('/auth/me');
    return res.json();
  }
};

// ========== Users ==========

export const usersAPI = {
  /**
   * Get all users
   */
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const res = await fetchWithAuth(`/users?${params}`);
    return res.json();
  },

  /**
   * Get user by ID
   */
  getById: async (id) => {
    const res = await fetchWithAuth(`/users/${id}`);
    return res.json();
  },

  /**
   * Create new user
   */
  create: async (userData) => {
    const res = await fetchWithAuth('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    return res.json();
  },

  /**
   * Update user
   */
  update: async (id, userData) => {
    const res = await fetchWithAuth(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
    return res.json();
  },

  /**
   * Delete user
   */
  delete: async (id) => {
    const res = await fetchWithAuth(`/users/${id}`, { method: 'DELETE' });
    return res.json();
  },

  /**
   * Assign role to user
   */
  assignRole: async (userId, roleId) => {
    const res = await fetchWithAuth(`/users/${userId}/role`, {
      method: 'POST',
      body: JSON.stringify({ roleId })
    });
    return res.json();
  }
};

// ========== Clients ==========

export const clientsAPI = {
  /**
   * Get all clients
   */
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const res = await fetchWithAuth(`/clients?${params}`);
    return res.json();
  },

  /**
   * Get client by ID
   */
  getById: async (id) => {
    const res = await fetchWithAuth(`/clients/${id}`);
    return res.json();
  },

  /**
   * Create new client
   */
  create: async (clientData) => {
    const res = await fetchWithAuth('/clients', {
      method: 'POST',
      body: JSON.stringify(clientData)
    });
    return res.json();
  },

  /**
   * Update client
   */
  update: async (id, clientData) => {
    const res = await fetchWithAuth(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(clientData)
    });
    return res.json();
  },

  /**
   * Delete client
   */
  delete: async (id) => {
    const res = await fetchWithAuth(`/clients/${id}`, { method: 'DELETE' });
    return res.json();
  }
};

// ========== Vouchers ==========

export const vouchersAPI = {
  /**
   * Get all vouchers
   */
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const res = await fetchWithAuth(`/vouchers?${params}`);
    return res.json();
  },

  /**
   * Get voucher by ID
   */
  getById: async (id) => {
    const res = await fetchWithAuth(`/vouchers/${id}`);
    return res.json();
  },

  /**
   * Create new voucher
   */
  create: async (voucherData) => {
    const res = await fetchWithAuth('/vouchers', {
      method: 'POST',
      body: JSON.stringify(voucherData)
    });
    return res.json();
  },

  /**
   * Update voucher
   */
  update: async (id, voucherData) => {
    const res = await fetchWithAuth(`/vouchers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(voucherData)
    });
    return res.json();
  },

  /**
   * Delete voucher
   */
  delete: async (id) => {
    const res = await fetchWithAuth(`/vouchers/${id}`, { method: 'DELETE' });
    return res.json();
  }
};

// ========== Chat Sessions ==========

export const chatAPI = {
  /**
   * Get all chat sessions
   */
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const res = await fetchWithAuth(`/chats?${params}`);
    return res.json();
  },

  /**
   * Get chat session by ID (room)
   */
  getById: async (roomId) => {
    const res = await fetchWithAuth(`/chats/${roomId}`);
    return res.json();
  },

  /**
   * Archive chat session
   */
  archive: async (roomId) => {
    const res = await fetchWithAuth(`/chats/${roomId}/archive`, {
      method: 'POST'
    });
    return res.json();
  }
};

// ========== Roles & Permissions ==========

export const rolesAPI = {
  /**
   * Get all roles
   */
  getAll: async () => {
    const res = await fetchWithAuth('/roles');
    return res.json();
  },

  /**
   * Get role by ID
   */
  getById: async (id) => {
    const res = await fetchWithAuth(`/roles/${id}`);
    return res.json();
  }
};

export const permissionsAPI = {
  /**
   * Get all permissions
   */
  getAll: async () => {
    const res = await fetchWithAuth('/permissions');
    return res.json();
  }
};

// ========== Statistics & Reports ==========

export const statsAPI = {
  /**
   * Get dashboard statistics
   */
  getDashboard: async () => {
    const res = await fetchWithAuth('/stats/dashboard');
    return res.json();
  },

  /**
   * Get chat statistics
   */
  getChat: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const res = await fetchWithAuth(`/stats/chats?${params}`);
    return res.json();
  },

  /**
   * Get queue statistics
   */
  getQueue: async () => {
    const res = await fetchWithAuth('/stats/queue');
    return res.json();
  }
};
